import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  MessageCircle,
  Loader2,
  AlertCircle,
  Wifi,
  WifiOff,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import {
  getConversationMessages,
  sendMessage,
  addReaction,
  getMessageReactions,
  subscribeToMessages,
  subscribeToReactions,
  unsubscribeFromMessages,
  unsubscribeFromReactions,
} from "../../lib/chatService";
import { useConversationCleanup } from "../../hooks/useChatCleanup";
import MessageBubble from "./MessageBubble";
import MessageInput from "./MessageInput";

const ChatConversation = ({ conversationId, brand, className = "" }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState("connected");
  const messagesEndRef = useRef(null);
  const conversationRef = useRef(null);

  // Ensure proper cleanup of conversation-specific subscriptions
  useConversationCleanup(conversationId);

  // Real-time connection status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setConnectionStatus("connected");
      console.log("🌐 Connection restored");
    };

    const handleOffline = () => {
      setIsOnline(false);
      setConnectionStatus("disconnected");
      console.log("🚫 Connection lost");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  // Load initial messages
  const loadMessages = useCallback(async () => {
    if (!conversationId || !user?.id) return;

    try {
      setLoading(true);
      setError(null);
      console.log(`📨 Loading messages for conversation: ${conversationId}`);

      const messagesData = await getConversationMessages(
        conversationId,
        user.id
      );

      // Ensure reactions are properly parsed if they come as JSONB
      const processedMessages = (messagesData || []).map((message) => ({
        ...message,
        reactions: Array.isArray(message.reactions)
          ? message.reactions
          : message.reactions
            ? JSON.parse(message.reactions)
            : [],
      }));

      setMessages(processedMessages);

      console.log(
        `✅ Loaded ${processedMessages.length} messages with reactions:`,
        processedMessages.map((m) => ({
          id: m.id,
          reactions: m.reactions?.length || 0,
        }))
      );

      // Scroll to bottom after loading messages
      setTimeout(scrollToBottom, 100);
    } catch (err) {
      console.error("❌ Error loading messages:", err);
      setError(err.message || "Failed to load messages");
    } finally {
      setLoading(false);
    }
  }, [conversationId, user?.id, scrollToBottom]);

  // Real-time message subscription
  useEffect(() => {
    console.log("🔍 DEBUGGING: Subscription useEffect triggered");
    console.log("🔍 conversationId:", conversationId);
    console.log("🔍 user?.id:", user?.id);
    console.log("🔍 isOnline:", isOnline);

    if (!conversationId || !user?.id || !isOnline) {
      console.log("❌ SUBSCRIPTION BLOCKED - Missing requirement:");
      console.log("   conversationId:", !!conversationId);
      console.log("   user?.id:", !!user?.id);
      console.log("   isOnline:", isOnline);
      return;
    }

    console.log(
      `🔔 Setting up real-time subscriptions for conversation: ${conversationId}`
    );
    console.log(`👤 User ID: ${user.id}`);
    console.log(`🌐 Online status: ${isOnline}`);
    setConnectionStatus("connecting");

    // Handle new messages
    const handleNewMessage = (newMessage) => {
      console.log("📨 Real-time message received:", newMessage);
      console.log("📨 Message sender:", newMessage.sender);
      console.log("📨 Message text:", newMessage.message_text);

      setMessages((prevMessages) => {
        // Check if message already exists (prevent duplicates)
        const messageExists = prevMessages.some(
          (msg) => msg.id === newMessage.id
        );
        if (messageExists) {
          console.log("⚠️ Duplicate message detected, skipping");
          return prevMessages;
        }

        const updatedMessages = [...prevMessages, newMessage];

        // Scroll to bottom for new messages
        setTimeout(scrollToBottom, 100);

        return updatedMessages;
      });

      setConnectionStatus("connected");
    };

    // Handle reaction updates
    const handleReactionUpdate = async (reactionData) => {
      console.log("👍 Real-time reaction update:", reactionData);

      try {
        // Reload reactions for the specific message
        const updatedReactions = await getMessageReactions(
          reactionData.messageId
        );

        setMessages((prevMessages) => {
          return prevMessages.map((message) => {
            if (message.id === reactionData.messageId) {
              console.log(
                `✅ Updated reactions for message ${reactionData.messageId}:`,
                updatedReactions
              );
              return { ...message, reactions: updatedReactions };
            }
            return message;
          });
        });
      } catch (err) {
        console.error("❌ Error reloading message reactions:", err);
      }
    };

    // Handle subscription errors
    const handleSubscriptionError = (error) => {
      console.error("❌ Real-time subscription error:", error);
      setConnectionStatus("error");
      setError(
        "Real-time connection failed. Messages may not update automatically."
      );
    };

    // Set up subscriptions
    const messageSubscription = subscribeToMessages(
      conversationId,
      handleNewMessage,
      handleSubscriptionError
    );

    const reactionSubscription = subscribeToReactions(
      conversationId,
      handleReactionUpdate,
      handleSubscriptionError
    );

    if (messageSubscription && reactionSubscription) {
      setConnectionStatus("connected");
    }

    // Cleanup subscriptions on unmount or dependency change
    return () => {
      console.log(
        `🔕 Cleaning up subscriptions for conversation: ${conversationId}`
      );
      unsubscribeFromMessages(conversationId);
      unsubscribeFromReactions(conversationId);
    };
  }, [conversationId, user?.id, isOnline, scrollToBottom]);

  // Load messages when conversation changes
  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Note: Messages are now marked as read when conversation is selected in dashboard
  // This ensures immediate UI feedback and prevents double-marking

  // Handle sending messages
  const handleSendMessage = async (
    messageText,
    messageType = "text",
    driveLink = null
  ) => {
    if (!conversationId || !user?.id || !messageText.trim()) return;

    try {
      setSending(true);
      console.log(`📤 Sending message to conversation: ${conversationId}`);
      console.log(`📤 Message text: "${messageText}"`);
      console.log(`📤 Sender ID: ${user.id}`);

      await sendMessage(
        conversationId,
        user.id,
        messageText,
        messageType,
        driveLink
      );

      console.log(
        "✅ Message sent successfully - should appear via real-time subscription"
      );
      // Note: The message will appear via real-time subscription, no need to manually add it
    } catch (err) {
      console.error("❌ Error sending message:", err);
      setError(err.message || "Failed to send message");

      // Show error briefly then clear it
      setTimeout(() => setError(null), 5000);
    } finally {
      setSending(false);
    }
  };

  // Handle message reactions
  const handleReaction = async (messageId, reactionType) => {
    if (!user?.id) return;

    try {
      console.log(
        `👍 Adding reaction: ${reactionType} to message: ${messageId}`
      );

      await addReaction(messageId, user.id, reactionType);
      console.log("✅ Reaction added successfully");
      // Note: The reaction will update via real-time subscription
    } catch (err) {
      console.error("❌ Error adding reaction:", err);
      setError(err.message || "Failed to add reaction");

      // Show error briefly then clear it
      setTimeout(() => setError(null), 3000);
    }
  };

  // Retry loading messages
  const handleRetry = () => {
    setError(null);
    loadMessages();
  };

  // Connection status indicator
  const ConnectionStatus = () => {
    if (!isOnline) {
      return (
        <div className="flex items-center gap-2 px-3 py-2 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300 text-sm">
          <WifiOff className="h-4 w-4" />
          <span>No internet connection</span>
        </div>
      );
    }

    if (connectionStatus === "connecting") {
      return (
        <div className="flex items-center gap-2 px-3 py-2 bg-yellow-500/20 border border-yellow-500/30 rounded-lg text-yellow-300 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Connecting to real-time chat...</span>
        </div>
      );
    }

    if (connectionStatus === "error") {
      return (
        <div className="flex items-center gap-2 px-3 py-2 bg-orange-500/20 border border-orange-500/30 rounded-lg text-orange-300 text-sm">
          <AlertCircle className="h-4 w-4" />
          <span>Real-time updates may be delayed</span>
        </div>
      );
    }

    if (connectionStatus === "connected") {
      return (
        <div className="flex items-center gap-2 px-3 py-2 bg-green-500/20 border border-green-500/30 rounded-lg text-green-300 text-sm">
          <Wifi className="h-4 w-4" />
          <span>Real-time chat connected</span>
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full space-y-4">
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-8 text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#B8460E] mx-auto mb-4" />
          <p className="text-white text-lg font-medium">
            Loading conversation...
          </p>
          <p className="text-gray-400 text-sm mt-2">
            Setting up real-time chat
          </p>
        </div>
      </div>
    );
  }

  if (error && !messages.length) {
    return (
      <div className="flex flex-col items-center justify-center h-full space-y-4">
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-red-500/20 p-8 text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <p className="text-white text-lg font-medium mb-2">
            Failed to load conversation
          </p>
          <p className="text-gray-400 text-sm mb-4">{error}</p>
          <button
            onClick={handleRetry}
            className="bg-[#B8460E] hover:bg-[#8B3A0E] text-white px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full ${className}`} ref={conversationRef}>
      {/* Connection Status (only show if not connected or has issues) */}
      {(connectionStatus !== "connected" || !isOnline) && (
        <div className="p-4 border-b border-white/10">
          <ConnectionStatus />
        </div>
      )}

      {/* Error Message (non-blocking) */}
      {error && messages.length > 0 && (
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-2 px-3 py-2 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300 text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-300 hover:text-red-200"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-8 text-center max-w-md">
              <MessageCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">
                Start the conversation
              </h3>
              <p className="text-gray-400">
                Send your first message to begin collaborating on{" "}
                {brand === "holistic_hercules"
                  ? "Holistic Hercules"
                  : "Aphrodite"}{" "}
                creative assets.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message, index) => {
              const previousMessage = index > 0 ? messages[index - 1] : null;
              const isConsecutive =
                previousMessage &&
                previousMessage.sender_id === message.sender_id &&
                previousMessage.message_type !== "system_notification" &&
                message.message_type !== "system_notification";

              return (
                <MessageBubble
                  key={message.id}
                  message={message}
                  currentUserId={user?.id}
                  onReaction={handleReaction}
                  isConsecutive={isConsecutive}
                />
              );
            })}
            {/* Scroll anchor */}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Message Input */}
      <div className="border-t border-white/10 p-4">
        <MessageInput
          onSendMessage={handleSendMessage}
          brand={brand}
          disabled={sending || !isOnline}
          placeholder={
            !isOnline
              ? "Connect to internet to send messages..."
              : sending
                ? "Sending..."
                : "Type your message or paste a Google Drive link..."
          }
        />

        {/* Real-time status indicator */}
        {isOnline && connectionStatus === "connected" && (
          <div className="flex items-center justify-center mt-2">
            <div className="flex items-center gap-2 text-xs text-green-400">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span>Real-time chat active</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatConversation;
